// The completable, documentable capability surface for completion, hover and
// the Reference panel, built from the native capability declarations: the
// standard three (`print`, `clock`, `random`) come from the tooling build's
// own `standardCapabilities()` - never hand copied - and Studio's engine-bound
// five from `nativeCapabilities.ts`, the very declarations a real check and
// run use. If either set ever grows a member, this catalog picks it up with no
// edit here.
//
// Signature text is the checker's own rendering, not a second type printer:
// the catalog analyses one small probe program that calls every declared
// function with no arguments and reads each callee's type back through
// `Analysis.hover`, which answers `effect fn(String, Int): String` for a
// capability function. The call is deliberately wrong (no arguments), since
// only the callee's type is read, and "a program with errors analyses fine"
// (`crates/hollowscript-wasm/web/README.md`, "The analysis handle").
//
// Loaded asynchronously, once: it needs the lazily loaded tooling wasm. A
// caller that cannot wait (a React render) uses Studio's `useCapabilityCatalog`
// hook (`src/studio/editor/code/useCapabilityCatalog.ts`), which wraps these.

import type { NativeCapabilityDeclaration } from '../native/hollowscript.js';
import {
  narrowGrantedCapabilities as narrowGrantedCapabilitiesImpl,
  STANDARD_CAPABILITY_NAMES,
  STUDIO_CAPABILITY_NAMES,
  type StudioCapabilityName,
} from '../capabilityNames.js';
import { NATIVE_CAPABILITY_DECLARATIONS } from '../nativeCapabilities.js';
import { getMonacoTooling, type MonacoTooling } from '../nativeTooling.js';

export interface CapabilityMember {
  readonly name: string;
  /** `spawn(String, Int, Float, Float, Float): String`: the member's name
   * followed by the type text the checker renders for it. */
  readonly signature: string;
}

export interface CapabilityEntry {
  readonly name: StudioCapabilityName;
  /** `function` for a capability that is a single function (`print`),
   * `namespace` for one with named members (`clock`, `entities`). */
  readonly kind: 'function' | 'namespace';
  /** The call signature of a `function` capability (`print(String)`);
   * `undefined` for a namespace. */
  readonly signature: string | undefined;
  readonly members: readonly CapabilityMember[];
}

export interface CapabilityCatalog {
  /** Every v1 capability, keyed by `uses` name. */
  readonly entries: ReadonlyMap<string, CapabilityEntry>;
  /** Every reserved word a program can write today, from the lexer's own
   * table (the words reserved for a later version are left out). */
  readonly keywords: readonly string[];
}

/** The checker renders a capability function's type with this prefix. */
const EFFECT_FN_PREFIX = 'effect fn';

/** Every declaration the catalog covers: the native standard set, then
 * Studio's own engine-bound capabilities in `STUDIO_CAPABILITY_NAMES` order. */
function allDeclarations(tooling: Pick<MonacoTooling, 'standardCapabilities'>): NativeCapabilityDeclaration[] {
  const declarations: NativeCapabilityDeclaration[] = [...tooling.standardCapabilities()];
  for (const name of STUDIO_CAPABILITY_NAMES) {
    const own = NATIVE_CAPABILITY_DECLARATIONS[name];
    if (own) declarations.push(...own);
  }
  return declarations;
}

/** Builds the catalog against `tooling`. Throws only if the tooling build
 * itself fails (a bug in it, not in the input). */
export function buildCapabilityCatalog(tooling: Pick<MonacoTooling, 'standardCapabilities' | 'analyse' | 'keywords'>): CapabilityCatalog {
  const declarations = allDeclarations(tooling);
  const names = [...new Set(declarations.map((d) => d.name))];

  // One probe line per declared function, remembering where its callee ends
  // so the hover lands inside the member expression.
  const lines = [`uses ${names.join(', ')}`];
  const probes: { declaration: NativeCapabilityDeclaration; offset: number }[] = [];
  let length = lines[0]!.length + 1;
  for (const declaration of declarations) {
    const callee = declaration.function === undefined ? declaration.name : `${declaration.name}.${declaration.function}`;
    const line = `effect ${callee}()`;
    probes.push({ declaration, offset: length + 'effect '.length + callee.length - 1 });
    lines.push(line);
    length += line.length + 1;
  }

  const types = new Map<NativeCapabilityDeclaration, string>();
  const analysis = tooling.analyse(`${lines.join('\n')}\n`, 'capability-catalog.hws', declarations, {});
  try {
    for (const { declaration, offset } of probes) {
      const type = analysis.hover(offset)?.type;
      if (type?.startsWith(EFFECT_FN_PREFIX)) types.set(declaration, type.slice(EFFECT_FN_PREFIX.length));
    }
  } finally {
    analysis.dispose();
  }

  const entries = new Map<string, CapabilityEntry>();
  for (const name of names) {
    if (!(STUDIO_CAPABILITY_NAMES as readonly string[]).includes(name)) continue;
    const own = declarations.filter((d) => d.name === name);
    const single = own.find((d) => d.function === undefined);
    entries.set(name, {
      name: name as StudioCapabilityName,
      kind: single ? 'function' : 'namespace',
      signature: single ? `${name}${types.get(single) ?? '()'}` : undefined,
      members: single
        ? []
        : own.map((d) => ({ name: d.function!, signature: `${d.function}${types.get(d) ?? '()'}` })),
    });
  }

  return {
    entries,
    keywords: tooling
      .keywords()
      .filter((keyword) => !keyword.future)
      .map((keyword) => keyword.word),
  };
}

let catalogPromise: Promise<CapabilityCatalog> | undefined;
let catalogLoaded: CapabilityCatalog | undefined;

/** The catalog, built once against the shared main-thread tooling build. */
export function loadCapabilityCatalog(): Promise<CapabilityCatalog> {
  catalogPromise ??= getMonacoTooling()
    .then((tooling) => buildCapabilityCatalog(tooling))
    .then((catalog) => {
      catalogLoaded = catalog;
      return catalog;
    })
    .catch((error: unknown) => {
      // A failed load must not be cached forever: the next caller retries.
      catalogPromise = undefined;
      throw error;
    });
  return catalogPromise;
}

/** The catalog if it has already loaded, else `undefined`. */
export function peekCapabilityCatalog(): CapabilityCatalog | undefined {
  return catalogLoaded;
}

/** `entities` -> [`spawn(String, Int, Float, Float, Float): String`, ...]; a
 * capability that is a bare function (`print`) reports zero members. */
export function capabilityMembers(catalog: CapabilityCatalog, capabilityName: string): readonly CapabilityMember[] {
  return catalog.entries.get(capabilityName)?.members ?? [];
}

/** A one-line hover/completion-detail description of a capability name
 * itself (e.g. `entities`), before drilling into a member. */
export function capabilityNamespaceDetail(catalog: CapabilityCatalog, capabilityName: string): string | undefined {
  const entry = catalog.entries.get(capabilityName);
  if (entry === undefined) return undefined;
  if (entry.kind === 'function') return `capability · ${entry.signature}`;
  return `capability · ${entry.members.map((member) => member.name).join(', ')}`;
}

/** A short, human description of what each capability is for - shown in
 * the Code workspace's Reference panel and completion detail, next to the
 * real signature. Kept here, beside the catalog it describes, so a new
 * capability added to `capabilityNames.ts` fails to build until it has a
 * description too. */
export const CAPABILITY_DESCRIPTIONS: Readonly<Record<StudioCapabilityName, string>> = {
  print: 'Write a line to the Output panel (or the console in Preview).',
  clock: 'Read the current time and date, as text or as milliseconds.',
  random: 'Random integers and floats.',
  entities: 'Spawn, read, move and remove objects in the running scene.',
  variables: 'Read and write the Logic variables your rules use.',
  audio: 'Play a one-shot sound through the scene\'s audio engine.',
  logic: 'Trigger the same effects a Logic rule can: announce, quests, items, win or lose.',
  input: 'Read which player actions are held down right now.',
};

/** Whether `name` is always granted to every script (`print`, `clock`,
 * `random`), whatever a project declares. */
export function isAlwaysOnCapability(name: string): boolean {
  return (STANDARD_CAPABILITY_NAMES as readonly string[]).includes(name);
}

/** The one narrowing rule for a project's declared grant, re-exported from
 * `capabilityNames.ts` so the editor's completion/hover/diagnostics read the
 * exact same rule the runtime applies (`print`/`clock`/`random` always
 * granted, engine-bound names narrowed to the declared subset). */
export const narrowGrantedCapabilities = narrowGrantedCapabilitiesImpl;

/** The capability names actually offered right now: every v1 name when
 * nothing has narrowed the grant, else the narrowed set intersected with
 * `STUDIO_CAPABILITY_NAMES` (already guaranteed by `narrowGrantedCapabilities`,
 * intersected again here defensively since callers may pass a set built by
 * other means). */
export function grantedCapabilityNames(granted: ReadonlySet<StudioCapabilityName> | undefined): readonly StudioCapabilityName[] {
  if (granted === undefined) return STUDIO_CAPABILITY_NAMES;
  return STUDIO_CAPABILITY_NAMES.filter((name) => granted.has(name));
}
